import React, { useState } from "react";
import { Link } from "react-router-dom";
import FeedbackModal from "../common/FeedbackModal";
import { useTranslation } from "react-i18next";
import { useBranding } from "../../context/BrandingContext";
import BRANDING_FALLBACK from "../../branding";

const Footer = ({ handleClickWhatsApp }) => {
  const { branding } = useBranding();
  const BRANDING = branding || BRANDING_FALLBACK;
  const { t, i18n } = useTranslation();
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const isAr = i18n.language?.startsWith('ar');

  return (
    <footer
      className="border-t mt-20"
      style={{ backgroundColor: 'var(--footer-bg)', color: 'var(--footer-text)' }}
    >
      <div className="max-w-7xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-10">

        {/* Brand */}
        <div>
          <Link to="/" className="flex items-center mb-4">
            {BRANDING.logo.path ? (
              <img
                src={BRANDING.logo.path}
                alt={BRANDING.logo.alt}
                className="h-20 w-auto object-contain transition-transform hover:scale-105"
              />
            ) : (
              <h2 className="text-2xl font-bold">
                {isAr ? (
                  <>{BRANDING.appName.ar.split(' ')[0]}<span className="text-brand-primary"> {BRANDING.appName.ar.split(' ').slice(1).join(' ')}</span></>
                ) : (
                  <>{BRANDING.appName.en.split(' ').slice(0, -1).join(' ')} <span className="text-brand-primary">{BRANDING.appName.en.split(' ').slice(-1)}</span></>
                )}
              </h2>
            )}
          </Link>
          <p className="text-gray-600 text-sm">
            {BRANDING.footerDescription?.[isAr ? 'ar' : 'en'] || ""
              //  t('footer.description')
            }
          </p>
          <div className="flex gap-4 mt-6">
            {BRANDING.socialLinks.facebook && (
              <a
                href={BRANDING.socialLinks.facebook}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.fbq) window.fbq('track', 'Contact', { content_name: 'Footer Facebook Click' });
                  if (window.ttq) window.ttq.track('Contact');
                }}
                className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md"
                title="Facebook"
              >
                <i className="fab fa-facebook-f text-lg"></i>
              </a>
            )}
            {BRANDING.socialLinks.instagram && (
              <a
                href={BRANDING.socialLinks.instagram}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.fbq) window.fbq('track', 'Contact', { content_name: 'Footer Instagram Click' });
                  if (window.ttq) window.ttq.track('Contact');
                }}
                className="w-10 h-10 rounded-full bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-600 text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md"
                title="Instagram"
              >
                <i className="fab fa-instagram text-lg"></i>
              </a>
            )}
            {BRANDING.socialLinks.tiktok && (
              <a
                href={BRANDING.socialLinks.tiktok}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  if (window.fbq) window.fbq('track', 'Contact', { content_name: 'Footer TikTok Click' });
                  if (window.ttq) window.ttq.track('Contact');
                }}
                className="w-10 h-10 rounded-full bg-black text-white flex items-center justify-center hover:scale-110 transition-transform shadow-md"
                title="TikTok"
              >
                <i className="fab fa-tiktok text-lg"></i>
              </a>
            )}
          </div>
        </div>

        {/* Links */}
        <div>
          <h3 className="font-semibold mb-4 text-gray-800">{t('footer.quick_links')}</h3>
          <ul className="space-y-2 text-sm text-gray-600">
            <li><Link to="/" className="hover:text-brand-primary transition-colors">{t('header.home')}</Link></li>
            <li><Link to="/shop" className="hover:text-brand-primary transition-colors">{t('header.shop')}</Link></li>
            <li><Link to="/about" className="hover:text-brand-primary transition-colors">{t('header.about')}</Link></li>
            <li><Link to="/contact" className="hover:text-brand-primary transition-colors">{t('header.contact')}</Link></li>

            {/* Dynamic Policy Links */}
            {BRANDING.activeLegals?.includes('privacy') && (
              <li><Link to="/privacy-policy" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.privacy.title')}</Link></li>
            )}
            {BRANDING.activeLegals?.includes('terms') && (
              <li><Link to="/terms" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.terms.title')}</Link></li>
            )}
            {BRANDING.activeLegals?.includes('refund') && (
              <li><Link to="/refund-policy" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.refund.title')}</Link></li>
            )}
            {BRANDING.activeLegals?.includes('shipping') && (
              <li><Link to="/shipping-policy" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.shipping.title')}</Link></li>
            )}
            {BRANDING.activeLegals?.includes('cookies') && (
              <li><Link to="/cookies" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.cookies.title')}</Link></li>
            )}
            {BRANDING.activeLegals?.includes('imprint') && (
              <li><Link to="/imprint" className="hover:text-brand-primary transition-colors">{t('admin.legal.pages.imprint.title')}</Link></li>
            )}
          </ul>
        </div>

        {/* Contact */}
        <div className="flex flex-col gap-4">
          <h3 className="font-bold text-gray-800 mb-1">{t('footer.contact')}</h3>
          <div className="flex flex-col gap-3 text-sm text-gray-600">
            <div className={`flex flex-col gap-2 ${isAr ? 'items-start' : 'items-start'}`}>
              <a href={`tel:${BRANDING.contact.phoneFooter}`} className="hover:text-brand-primary transition-colors flex items-center gap-2 group">
                <span className="text-lg group-hover:scale-110 transition-transform">📞</span>
                <span dir="ltr" className="group-hover:translate-x-1 transition-transform inline-block">{BRANDING.contact.phoneFooter}</span>
              </a>
            </div>

            <a
              href={`https://wa.me/${BRANDING.socialLinks.whatsapp.replace(/[^0-9]/g, '')}?text=${encodeURIComponent(isAr ? 'مرحبا، أريد الاستفسار عن منتج' : 'Hello, I would like to inquire about a product')}`}
              onClick={handleClickWhatsApp}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-green-600 transition-colors flex items-center gap-2 group">
              <span className="text-lg group-hover:rotate-12 transition-transform">💬</span>
              <span className="group-hover:translate-x-1 transition-transform inline-block">{t('footer.whatsapp')}</span>
            </a>

            <div className="flex items-center gap-2">
              <a href={`mailto:${BRANDING.contact.email}`} className="hover:text-brand-primary transition-colors flex items-center gap-2 group">
                <span className="text-lg group-hover:scale-110 transition-transform">✉</span>
                <span dir="ltr" className="group-hover:translate-x-1 transition-transform inline-block">{BRANDING.contact.email}</span>
              </a>
            </div>
          </div>

          <div>
            <button
              onClick={() => setFeedbackOpen(true)}
              className="mt-2 text-brand-primary hover:text-brand-primary-hover text-sm font-medium underline decoration-brand-primary/20 underline-offset-4 transition-colors"
            >
              {t('footer.feedback')}
            </button>
          </div>
        </div>
      </div>

      <div
        className="border-t text-center py-6 text-xs"
        style={{ backgroundColor: 'rgba(255,255,255,0.03)', opacity: 0.8 }}
      >
        © {new Date().getFullYear()} {isAr ? BRANDING.appName.ar : BRANDING.appName.en}. {t('footer.rights')}
      </div>

      <FeedbackModal open={feedbackOpen} onClose={() => setFeedbackOpen(false)} />
    </footer>
  );
};

export default Footer;
